import { Box, DialogContent, Typography } from "@mui/material";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import { useParams } from "react-router-dom";
import { RootState } from "../../../../../store/store";
import {
  useCallback,
  useState,
  SetStateAction,
  Dispatch,
  useEffect,
} from "react";
import MessageDialog from "../../../../../components/MessageDialog/MessageDialog";
import {
  IAsyncOptionFn,
  IDynamicFormFieldsJson,
} from "../../../../../models/user/DynamicForm/DynamicForm";
import {
  GetBOPUsageAndOccupancyFormJson,
  UpdateBOPUsageAndOccupancy,
} from "../../../../../store/user/policyChange/BOPPolicyChangeActions";
import DynamicForm from "../../../../../common/DynamicForm/DynamicForm";
import { GetPolicyRisks, GetPolicyDetailsHeader } from "../../../../../store/user/policyDetails/policyDetailsActions";
import { setBOPUsageAndOccupancyFormFieldsJson } from "../../../../../store/user/policyChange/BOPPolicyChangeSlice";
import { getCookie, formatDateToYYYYMMDD } from "../../../../../utils/Utility";
import { setCalledReducerType } from "../../../../../store/common/CommonSlice";
import { IBOPUsageAndOccupancyUpdateRequestParams } from "../../../../../models/user/PolicyChange/BOPPolicyChange";

interface IProps {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  changePolicyId?: string;
  changePolicyLobCode?: string;
  onClose?: () => void;
}

const UpdateUsageAndOccupancy: React.FC<IProps> = ({
  open,
  setOpen,
  changePolicyId,
  changePolicyLobCode,
  onClose,
}) => {
  const dispatch = useAppDispatch();

  let { policyId, lobCode } = useParams();
  policyId = policyId ? policyId : changePolicyId;
  lobCode = lobCode ? lobCode : changePolicyLobCode;

  const carrier = getCookie("carrier");
  const system = getCookie("system");

  const { userDetails } = useAppSelector((state: RootState) => state.userAuth);
  const { policyDetailsHeader } = useAppSelector((state: RootState) => state.policyDetails);

  useEffect(() => {
    if (policyId && carrier && system && lobCode) {
      dispatch(GetPolicyRisks({ policyId, carrier, system, lobCode }));
      dispatch(GetPolicyDetailsHeader({ policyId, carrier, system, lobCode }));
    }
  }, [policyId, carrier, system, lobCode, dispatch]);

  const { bopUsageAndOccupancyFormFieldsJson } = useAppSelector(
    (state: RootState) => state.bopPolicyChange
  );

  const { isSuccess, isError, calledReducerType } = useAppSelector(
    (state: RootState) => state.common
  );

  const [formFieldsJson, setFormFieldsJson] = useState<IDynamicFormFieldsJson | null>(null);
  const [messageDialogOpen, setMessageDialogOpen] = useState<boolean>(false);

  const asyncFunctions: { [key: string]: IAsyncOptionFn } = {
    // Add async option resolvers if required
  };

  const handleClose = useCallback(() => {
    dispatch(setBOPUsageAndOccupancyFormFieldsJson(null));
    setOpen(false);
  }, [setOpen, dispatch]);

  const handleMessageDialogClose = useCallback(() => {
    setMessageDialogOpen(false);
    dispatch(setCalledReducerType(null));
    if (onClose) {
      onClose();
    } else {
      handleClose();
    }
  }, [dispatch, handleClose, onClose]);

  const handleUpdateUsageAndOccupancyOnSubmit = (formValues: { [key: string]: any }) => {
    if (
      policyDetailsHeader &&
      policyDetailsHeader?.carrierPolicyNumber &&
      userDetails &&
      userDetails.customerId &&
      policyId &&
      system &&
      carrier &&
      lobCode
    ) {
      const changeDate = formValues.effectiveDate
        ? formatDateToYYYYMMDD(new Date(formValues.effectiveDate))
        : formatDateToYYYYMMDD(new Date());

      const requestParams: IBOPUsageAndOccupancyUpdateRequestParams = {
        carrierPolicyNo: policyDetailsHeader?.carrierPolicyNumber,
        changeDt: changeDate,
        customerId: userDetails?.customerId,
        policyRef: policyId,
        carrier: carrier,
        system: system,
        lobCode: lobCode,
        detailDescription: "Updating BOP usage & occupancy from UI",
        usageAndOccupancy: {
          occupancyType: formValues.occupancyType,
          yearroofupdated: String(formValues.yearroofupdated ?? formValues.yearRoofUpdated ?? ""),
        },
      };

      dispatch(UpdateBOPUsageAndOccupancy(requestParams));
    }
  };

  useEffect(() => {
    if (
      (isSuccess || isError) &&
      calledReducerType === "bopPolicyChange/UpdateBOPUsageAndOccupancy"
    ) {
      setMessageDialogOpen(true);
      dispatch(setCalledReducerType(null));
    }
  }, [isSuccess, isError, calledReducerType, dispatch]);

  useEffect(() => {
    if (open) {
      dispatch(GetBOPUsageAndOccupancyFormJson());
      dispatch(setCalledReducerType(null));
    }
  }, [dispatch, open]);

  useEffect(() => {
    if (bopUsageAndOccupancyFormFieldsJson) {
      setFormFieldsJson(bopUsageAndOccupancyFormFieldsJson);
    }
  }, [bopUsageAndOccupancyFormFieldsJson]);

  return (
    <>
      <MessageDialog handleClose={handleMessageDialogClose} open={messageDialogOpen} />
      <DialogContent id="update_usage_and_occupancy_form_content" data-test-id="update_usage_and_occupancy_form_content" aria-label="Update usage and occupancy form content">
        {formFieldsJson ? (
          <DynamicForm
            formFieldsJson={formFieldsJson}
            onFormSubmit={handleUpdateUsageAndOccupancyOnSubmit}
            asyncOptionsFunctions={asyncFunctions}
          />
        ) : (
          <Box sx={{ padding: "2rem", textAlign: "center" }}>
            <Typography>Loading form...</Typography>
          </Box>
        )}
      </DialogContent>
    </>
  );
};

export default UpdateUsageAndOccupancy; 